/**
 * Pricing Page
 *
 * Premium dark futuristic pricing page with glassmorphism cards,
 * annual/monthly toggle, usage examples, and animated CTA buttons.
 *
 * Design: dark canvas, orange glow accents, frosted glass surfaces,
 * Framer Motion staggered reveals, gradient text for prices.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Button,
  Switch,
  SimpleGrid,
  Divider,
  Table,
  Loader,
} from '@mantine/core';
import {
  IconCheck,
  IconBolt,
  IconRocket,
  IconDiamond,
  IconSparkles,
  IconMail,
  IconCalendar,
  IconMessage,
  IconNote,
  IconWorld,
  IconArrowUpRight,
  IconArrowDownRight,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePlans, useBillingStatus, useCreateCheckout, useUpgradePlan, type Plan } from '../hooks/useBilling';
import { useAuthStore } from '../store/auth';
import { trackPricingPageViewed, trackPlanSelected, trackCheckoutStarted } from '../analytics';
import { SEO } from '../components/shared/SEO';

/* ── Design tokens ─────────────────────────────────────────────── */

const gradientText: React.CSSProperties = {
  background: 'linear-gradient(135deg, #fd7e14, #ff922b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const cardBase: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: 16,
  padding: '32px 28px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
};

const featuredCard: React.CSSProperties = {
  ...cardBase,
  border: '1px solid rgba(253, 126, 20, 0.3)',
  boxShadow: '0 0 40px rgba(253, 126, 20, 0.08), inset 0 1px 0 rgba(253,126,20,.15)',
};

const CSS = `
  .pricing-card { cursor: default; }
  .pricing-card:hover {
    transform: translateY(-4px);
    border-color: rgba(255,255,255,.12);
    box-shadow: 0 8px 40px rgba(0,0,0,.3);
  }
  .pricing-card.featured:hover {
    border-color: rgba(253,126,20,.5);
    box-shadow: 0 0 60px rgba(253,126,20,.12), 0 8px 40px rgba(0,0,0,.3);
  }
  @keyframes glow-pulse {
    0%, 100% { box-shadow: 0 0 20px rgba(253,126,20,.3); }
    50% { box-shadow: 0 0 30px rgba(253,126,20,.5); }
  }
  .glow-btn { animation: glow-pulse 2s ease-in-out infinite; }
  .glow-btn:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 0 35px rgba(253,126,20,.5);
  }
`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.1, ease: [0.25, 0.4, 0.25, 1] as [number, number, number, number] },
  }),
};

/* ── Tier meta (icons, features, descriptions) ─────────────────── */

const tierMeta: Record<string, {
  icon: React.ReactNode;
  tagline: string;
  features: string[];
  recommended?: boolean;
  slots?: number;
}> = {
  free: {
    icon: <IconSparkles size={22} />,
    tagline: 'Get started for free',
    features: ['500 credits to try', 'Standard model', '1 agent', 'All integrations'],
  },
  explorer: {
    icon: <IconBolt size={22} />,
    tagline: 'For individuals who want more',
    features: [
      '50,000 credits/month',
      'Advanced model',
      '1 agent',
      'All integrations',
      'Priority support',
      'Early access to new features',
    ],
  },
  cobuilder: {
    icon: <IconRocket size={22} />,
    tagline: 'For power users and professionals',
    features: [
      '120,000 credits/month',
      'Advanced model',
      'Up to 3 agents',
      'Everything in Plus',
      'Premium model access',
      'Priority support',
      'Price locked for life',
    ],
    recommended: true,
  },
  believer: {
    icon: <IconDiamond size={22} />,
    tagline: 'For teams and heavy workloads',
    features: [
      '300,000 credits/month',
      'Premium models',
      'Up to 10 agents',
      'Everything in Pro',
      'Dedicated support',
      'Custom integrations',
      'Early access to future products',
      'Direct contact with founders',
    ],
  },
};

/* ── Usage examples ────────────────────────────────────────────── */

const usageExamples = [
  { icon: <IconMessage size={16} />, task: 'Quick Q&A (weather, reminder)', model: 'Standard', credits: '~50-100', duration: 'instant' },
  { icon: <IconMail size={16} />, task: 'Email draft with Gmail skill', model: 'Advanced', credits: '~150-200', duration: '~30 sec' },
  { icon: <IconCalendar size={16} />, task: 'Calendar scheduling + research', model: 'Advanced', credits: '~300-400', duration: '~1 min' },
  { icon: <IconNote size={16} />, task: 'Create a Notion page from notes', model: 'Advanced', credits: '~400-600', duration: '1-2 min' },
  { icon: <IconWorld size={16} />, task: 'Web search and summarize results', model: 'Advanced', credits: '~500-900', duration: '2-3 min' },
];

/* ── Component ─────────────────────────────────────────────────── */

export default function PricingPage() {
  const [searchParams] = useSearchParams();
  const [isAnnual, setIsAnnual] = useState(searchParams.get('annual') === 'true');
  const [activePlanId, setActivePlanId] = useState<string | null>(null);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();
  const { data: plans, isLoading } = usePlans();
  const { data: billing } = useBillingStatus(isAuthenticated);
  const checkout = useCreateCheckout();
  const upgrade = useUpgradePlan();

  // Track pricing page view on mount
  useEffect(() => {
    trackPricingPageViewed();
  }, []);

  // Sort plans by sortOrder, excluding "free" from the paid tiers display
  const paidPlans = (plans ?? []).filter((p) => p.tier !== 'free').sort((a, b) => a.sortOrder - b.sortOrder);

  // Current subscription info
  const currentSub = billing?.subscription;
  const currentTier = currentSub?.status === 'active' ? currentSub.plan.tier : null;
  const currentInterval = currentSub?.billingInterval; // 'month' | 'year'
  const currentPlanSort = currentTier
    ? paidPlans.find((p) => p.tier === currentTier)?.sortOrder ?? -1
    : -1;

  // Pending downgrade info
  const pendingDG = billing?.pendingDowngrade ?? null;
  const pendingDGDate = pendingDG
    ? new Date(pendingDG.effectiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  // What interval the toggle is set to
  const selectedInterval = isAnnual ? 'year' : 'month';

  const getPlanAction = (plan: Plan): 'current' | 'upgrade' | 'downgrade' | 'subscribe' | 'scheduled' | 'switch-annual' | 'switch-monthly' => {
    // Free-tier users (no Stripe subscription) must go through checkout, not upgrade
    if (!isAuthenticated || !currentTier || currentTier === 'free') return 'subscribe';

    // Check if this plan + the selected interval matches the pending schedule
    const isScheduledTarget = pendingDG
      && plan.displayName === pendingDG.planName
      && selectedInterval === pendingDG.interval;

    if (plan.tier === currentTier) {
      // Same plan, but different billing interval?
      if (selectedInterval !== currentInterval) {
        if (isScheduledTarget) return 'scheduled';
        return isAnnual ? 'switch-annual' : 'switch-monthly';
      }
      return 'current';
    }
    // Different plan -- check if it's the scheduled downgrade target at matching interval
    if (isScheduledTarget) return 'scheduled';
    return plan.sortOrder > currentPlanSort ? 'upgrade' : 'downgrade';
  };

  const handlePlanAction = (plan: Plan) => {
    const action = getPlanAction(plan);
    if (action === 'current' || action === 'scheduled') return;

    // Track plan selection
    trackPlanSelected(plan.displayName);

    // Interval switches go through the upgrade endpoint too
    const effectiveAction = action === 'switch-annual' ? 'upgrade'
      : action === 'switch-monthly' ? 'downgrade'
      : action;

    if (!isAuthenticated) {
      // Encode plan + interval so login flow can auto-redirect to Stripe Checkout
      navigate(`/login?package=${plan.id}&interval=${isAnnual ? 'year' : 'month'}`);
      return;
    }

    if (effectiveAction === 'subscribe') {
      trackCheckoutStarted(plan.displayName, isAnnual ? 'year' : 'month');
      setActivePlanId(plan.id);
      checkout.mutate(
        { planId: plan.id, interval: isAnnual ? 'year' : 'month' },
        { onSettled: () => setActivePlanId(null) },
      );
    } else {
      // Upgrade, downgrade, or interval switch via the upgrade endpoint
      setActivePlanId(plan.id);
      upgrade.mutate(
        { planId: plan.id, interval: isAnnual ? 'year' : 'month' },
        {
          onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['billing'] });
            if (result.scheduled) {
              notifications.show({
                title: 'Downgrade scheduled',
                message: result.message,
                color: 'blue',
                autoClose: 8000,
              });
              navigate('/billing');
            } else {
              navigate(`/billing?plan_changed=${effectiveAction}`);
            }
          },
          onSettled: () => setActivePlanId(null),
        },
      );
    }
  };

  const formatPrice = (plan: Plan) => {
    if (isAnnual && plan.annualPriceCents) {
      return Math.round(plan.annualPriceCents / 12 / 100);
    }
    return plan.monthlyPriceCents / 100;
  };

  const formatCredits = (credits: number) => {
    if (credits >= 1000) return `${(credits / 1000).toFixed(0)}k`;
    return credits.toString();
  };

  return (
    <Box style={{ position: 'relative', zIndex: 1 }}>
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for your personal AI agent. Start free with 500 credits. Upgrade for more messages, integrations, and priority support."
        path="/pricing"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Haive Pricing',
          'url': 'https://haive.dev/pricing',
          'description': 'Simple, transparent pricing for your personal AI agent. Start free with 500 credits.',
          'isPartOf': {
            '@type': 'WebSite',
            'name': 'Haive',
            'url': 'https://haive.dev',
          },
        }}
      />
      <style>{CSS}</style>

      <Container size={1100} py={{ base: 60, md: 100 }} mt={72} px="md">
        <Stack gap={60}>

          {/* ══════ Hero ══════ */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <Stack align="center" gap={12}>
              <Badge
                variant="outline"
                color="orange"
                size="lg"
                style={{
                  borderColor: 'rgba(253,126,20,.3)',
                  color: '#fd7e14',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Alpha Access
              </Badge>
              <Title
                order={1}
                ta="center"
                style={{
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  fontWeight: 800,
                  letterSpacing: '-.03em',
                  lineHeight: 1.1,
                }}
              >
                Choose your{' '}
                <span style={gradientText}>Haive plan</span>
              </Title>
              <Text size="lg" c="dimmed" ta="center" maw={600} lh={1.6}>
                Join the alpha and help shape the future of AI assistants.
                Every plan includes all integrations and features.
              </Text>
            </Stack>
          </motion.div>

          {/* ══════ Annual Toggle ══════ */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
            <Group justify="center" gap="md">
              <Text size="sm" fw={isAnnual ? 400 : 600} c={isAnnual ? 'dimmed' : 'white'}>
                Monthly
              </Text>
              <Switch
                size="lg"
                color="orange"
                checked={isAnnual}
                onChange={(e) => setIsAnnual(e.currentTarget.checked)}
                styles={{
                  track: {
                    cursor: 'pointer',
                  },
                }}
              />
              <Group gap={6}>
                <Text size="sm" fw={isAnnual ? 600 : 400} c={isAnnual ? 'white' : 'dimmed'}>
                  Annual
                </Text>
                <Badge size="sm" color="green" variant="filled" radius="xl">
                  Save 17%
                </Badge>
              </Group>
            </Group>
          </motion.div>

          {/* ══════ Plan Cards ══════ */}
          {isLoading ? (
            <Group justify="center" py={60}>
              <Loader color="orange" size="lg" />
            </Group>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={20}>
              {paidPlans.map((plan, idx) => {
                const meta = tierMeta[plan.tier] ?? tierMeta.explorer;
                const isFeatured = meta.recommended;
                const price = formatPrice(plan);

                return (
                  <motion.div
                    key={plan.id}
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={idx + 2}
                    style={{ display: 'flex' }}
                  >
                    <Box
                      className={`pricing-card ${isFeatured ? 'featured' : ''}`}
                      style={{ ...(isFeatured ? featuredCard : cardBase), display: 'flex', flexDirection: 'column', flex: 1 }}
                    >
                      {/* Featured badge */}
                      {isFeatured && (
                        <Badge
                          color="orange"
                          variant="filled"
                          size="sm"
                          radius="xl"
                          style={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                          }}
                        >
                          Most Popular
                        </Badge>
                      )}

                      <Stack gap={20} style={{ flex: 1 }}>
                        {/* Tier icon + name */}
                        <Group gap={10}>
                          <Box style={{ color: '#fd7e14' }}>{meta.icon}</Box>
                          <Text fw={700} size="lg">{plan.displayName}</Text>
                        </Group>

                        <Text size="sm" c="dimmed" lh={1.5}>
                          {meta.tagline}
                        </Text>

                        {/* Price */}
                        <Box>
                          <Group gap={4} align="baseline">
                            <Title
                              order={2}
                              style={{
                                ...gradientText,
                                fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                                fontWeight: 800,
                                letterSpacing: '-.02em',
                              }}
                            >
                              €{price}
                            </Title>
                            <Text size="sm" c="dimmed">/mo</Text>
                          </Group>
                          <Text
                            size="xs"
                            c="dimmed"
                            mt={2}
                            style={{
                              visibility: isAnnual && plan.annualPriceCents ? 'visible' : 'hidden',
                            }}
                          >
                            €{plan.annualPriceCents ? (plan.annualPriceCents / 100).toFixed(0) : 0} billed annually
                          </Text>
                        </Box>

                        {/* Credits badge */}
                        <Badge
                          variant="light"
                          color="orange"
                          size="lg"
                          radius="md"
                          style={{ alignSelf: 'flex-start' }}
                        >
                          {formatCredits(plan.monthlyCredits)} credits/mo
                        </Badge>

                        <Divider color="rgba(255,255,255,.06)" />

                        {/* Features */}
                        <Stack gap={8} style={{ flex: 1 }}>
                          {meta.features.map((feature) => (
                            <Group key={feature} gap={8} wrap="nowrap">
                              <IconCheck size={16} color="#fd7e14" style={{ flexShrink: 0 }} />
                              <Text size="sm" c="dimmed">{feature}</Text>
                            </Group>
                          ))}
                        </Stack>

                        {/* Slots remaining */}
                        {meta.slots && (
                          <Text size="xs" c="dimmed" ta="center" mt={4}>
                            <span style={{ color: '#fd7e14', fontWeight: 600 }}>
                              {meta.slots}
                            </span>{' '}
                            alpha spots available
                          </Text>
                        )}

                        {/* CTA */}
                        {(() => {
                          const action = getPlanAction(plan);
                          const isCurrent = action === 'current';
                          const isScheduled = action === 'scheduled';
                          const isInactive = isCurrent || isScheduled;
                          const label = isCurrent
                            ? 'Current Plan'
                            : isScheduled
                              ? `Switching ${pendingDGDate}`
                              : action === 'switch-annual'
                                ? 'Switch to Annual'
                                : action === 'switch-monthly'
                                  ? 'Switch to Monthly'
                                  : action === 'upgrade'
                                    ? 'Upgrade'
                                    : action === 'downgrade'
                                      ? 'Downgrade'
                                      : isAuthenticated
                                        ? 'Subscribe'
                                        : 'Get Started';
                          const icon = (action === 'upgrade' || action === 'switch-annual')
                            ? <IconArrowUpRight size={18} />
                            : (action === 'downgrade' || action === 'switch-monthly')
                              ? <IconArrowDownRight size={18} />
                              : isScheduled
                                ? <IconCalendar size={18} />
                                : undefined;

                          return (
                            <Button
                              fullWidth
                              size="lg"
                              color={isInactive ? (isScheduled ? 'blue' : 'gray') : 'orange'}
                              className={isFeatured && !isInactive ? 'glow-btn' : ''}
                              variant={isInactive ? 'light' : isFeatured ? 'filled' : 'light'}
                              loading={activePlanId === plan.id}
                              disabled={isInactive || (activePlanId !== null && activePlanId !== plan.id)}
                              leftSection={icon}
                              onClick={() => handlePlanAction(plan)}
                              style={{
                                marginTop: 8,
                                fontWeight: 700,
                                transition: 'transform .15s, box-shadow .15s',
                              }}
                            >
                              {label}
                            </Button>
                          );
                        })()}
                      </Stack>
                    </Box>
                  </motion.div>
                );
              })}
            </SimpleGrid>
          )}

          {/* ══════ Usage Examples ══════ */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={5}>
            <Stack gap={20}>
              <Stack align="center" gap={4}>
                <Text
                  size="xs"
                  fw={700}
                  c="orange"
                  style={{ textTransform: 'uppercase', letterSpacing: '0.12em' }}
                >
                  Credit Usage Guide
                </Text>
                <Title order={3} ta="center" fw={700}>
                  What can your credits do?
                </Title>
                <Text size="sm" c="dimmed" ta="center" maw={500}>
                  Credits are consumed based on the AI model and task complexity.
                  Here are some real-world examples.
                </Text>
              </Stack>

              <Box
                style={{
                  ...cardBase,
                  padding: 0,
                  overflow: 'hidden',
                }}
              >
                <Table
                  striped={false}
                  highlightOnHover
                  styles={{
                    table: { borderCollapse: 'separate', borderSpacing: 0 },
                    thead: {
                      background: 'rgba(255,255,255,.02)',
                    },
                    th: {
                      color: 'rgba(255,255,255,.45)',
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'uppercase' as const,
                      letterSpacing: '0.06em',
                      borderBottom: '1px solid rgba(255,255,255,.06)',
                      padding: '12px 16px',
                    },
                    td: {
                      borderBottom: '1px solid rgba(255,255,255,.04)',
                      padding: '12px 16px',
                      fontSize: 14,
                    },
                    tr: {
                      transition: 'background .15s',
                    },
                  }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Task</Table.Th>
                      <Table.Th>AI Model</Table.Th>
                      <Table.Th>Credits</Table.Th>
                      <Table.Th>Duration</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {usageExamples.map((ex) => (
                      <Table.Tr key={ex.task}>
                        <Table.Td>
                          <Group gap={8}>
                            <Box style={{ color: '#fd7e14' }}>{ex.icon}</Box>
                            <Text size="sm">{ex.task}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="dot" color="orange" size="sm">
                            {ex.model}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={600} style={gradientText}>{ex.credits}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{ex.duration}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>

              <Text size="xs" c="dimmed" ta="center">
                Plus (50k/mo) ≈ 170-330 quick tasks or 55-100 email drafts per month
              </Text>
            </Stack>
          </motion.div>

          {/* ══════ Bottom CTA ══════ */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={6}>
            <Box
              style={{
                ...cardBase,
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,.08)',
              }}
            >
              {currentTier ? (
                <Stack align="center" gap={12}>
                  <Text fw={700} size="lg">Happy with your plan?</Text>
                  <Text size="sm" c="dimmed" maw={400}>
                    Your {currentSub?.plan.displayName} plan is active.
                    Head back to your dashboard and start chatting with your agent.
                  </Text>
                  <Button
                    variant="outline"
                    color="orange"
                    size="md"
                    onClick={() => navigate('/dashboard')}
                    style={{ marginTop: 4 }}
                  >
                    Back to Dashboard
                  </Button>
                </Stack>
              ) : (
                <Stack align="center" gap={12}>
                  <Text fw={700} size="lg">Not ready to commit?</Text>
                  <Text size="sm" c="dimmed" maw={400}>
                    Try Haive free with 500 credits — enough to see Gmail, Calendar,
                    and Telegram integrations in action.
                  </Text>
                  <Button
                    variant="outline"
                    color="orange"
                    size="md"
                    onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
                    style={{ marginTop: 4 }}
                  >
                    Try Free
                  </Button>
                </Stack>
              )}
            </Box>
          </motion.div>

        </Stack>
      </Container>
    </Box>
  );
}
